// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What each filing of a BOM scans (component design §4.4): the robot, then each module
 * nested under the one it is fitted into.
 *
 * The robot's count is what the final-assembly station scans — one code per module and
 * the serials outside every module. A module's count is its own serials plus one code per
 * lower module: the chassis scans its four wheels and its battery, not the wheels' motors.
 * Shown on the BOM page and again in the publish dialog, which is where §8.2 step 9 asks
 * for the numbers to be checked before they are frozen.
 */

import { useTranslation } from "react-i18next";
import type { BomModule, BomModules, ModuleType } from "@/api/types";

export function moduleTypeLabel(code: string, types: ModuleType[], english: boolean): string {
  const type = types.find((entry) => entry.code === code);
  const name = type ? (english && type.english_name) || type.name : "";
  return name ? `${code} ${name}` : code;
}

export function BomModulesSummary({
  modules,
  types,
}: {
  modules: BomModules;
  types: ModuleType[];
}) {
  const { t, i18n } = useTranslation();
  const english = i18n.language === "en-US";
  const label = (code: string) => moduleTypeLabel(code, types, english);
  const breakdown = (module: BomModule) => {
    const parts = [];
    if (module.required_sn_count > 0) {
      parts.push(t("bom.modules.serials", { n: module.required_sn_count }));
    }
    for (const slot of module.module_slots) {
      parts.push(
        t("bom.modules.moduleCodes", { n: slot.quantity, module: label(slot.module_type) }),
      );
    }
    return parts.join(" + ") || "—";
  };
  // The tree's order: each module right under the one it is fitted into.
  const ordered: BomModule[] = [];
  const place = (parentType: string | null) => {
    const scope =
      parentType === null
        ? modules.robot
        : modules.modules.find((module) => module.module_type === parentType);
    for (const slot of scope?.module_slots ?? []) {
      const child = modules.modules.find((module) => module.module_type === slot.module_type);
      if (child && !ordered.includes(child)) {
        ordered.push(child);
        place(child.module_type);
      }
    }
  };
  place(null);

  return (
    <table className="bom-modules">
      <thead>
        <tr>
          <th>{t("bom.modules.module")}</th>
          <th>{t("bom.modules.assembly")}</th>
          <th>{t("bom.modules.lines")}</th>
          <th>{t("bom.modules.scans")}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>{t("bom.modules.robot")}</strong>
          </td>
          <td>—</td>
          <td>{modules.robot.line_count}</td>
          <td>
            <strong>{modules.robot.scan_count}</strong>{" "}
            <span className="hint">{breakdown(modules.robot)}</span>
          </td>
        </tr>
        {ordered.map((module) => (
          <tr key={module.module_type}>
            <td style={{ paddingLeft: `${8 + module.depth * 16}px` }}>
              <span className="module-badge">{label(module.module_type ?? "")}</span>
            </td>
            <td>
              <span className="mono">{module.position_code}</span> {module.material_info}
            </td>
            <td>{module.line_count}</td>
            <td>
              <strong>{module.scan_count}</strong> <span className="hint">{breakdown(module)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
